import React, { createContext, useContext, useCallback } from 'react';
import { useLocalStorage } from '@vastra/ui';
import { useToastContext } from './ToastContext';

interface WishlistContextValue {
  wishlist: string[];
  wishlistCount: number;
  toggleWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
}

const WishlistContext = createContext<WishlistContextValue | null>(null);

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}

const WISHLIST_KEY = 'vastra_wishlist';

interface WishlistProviderProps {
  children: React.ReactNode;
}

export function WishlistProvider({ children }: WishlistProviderProps) {
  const [wishlist, setWishlist] = useLocalStorage<string[]>(WISHLIST_KEY, []);
  const { showToast } = useToastContext();

  const toggleWishlist = useCallback(
    (productId: string) => {
      setWishlist((prev) => {
        const has = prev.includes(productId);
        if (has) {
          showToast('Removed from wishlist');
          return prev.filter((id) => id !== productId);
        }
        showToast('Added to wishlist');
        return [...prev, productId];
      });
    },
    [setWishlist, showToast]
  );

  const isInWishlist = useCallback(
    (productId: string) => wishlist.includes(productId),
    [wishlist]
  );

  const value: WishlistContextValue = {
    wishlist,
    wishlistCount: wishlist.length,
    toggleWishlist,
    isInWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
}
